import React, { useState } from 'react';
import { Code, FileText, Grid } from 'react-feather';
import { ConfigEmptyState } from '../ConfigPages';

/**
 * What the OCR pass actually read, in three views: the tables it found, the
 * key–value pairs it recognised, and the raw text. This is the checkpoint
 * before mapping — if a table is missing here, no mapping will rescue it.
 */
export default function DataPreviewTabs({ extractedData, onNext, onBack, hideFooter = false }) {
  const [activeTab, setActiveTab] = useState('tables');

  const tables = extractedData?.tables || [];
  const pairs = extractedData?.keyValuePairs || {};
  const text = extractedData?.extractedText;

  const tabs = [
    { id: 'tables', label: 'Tables', icon: Grid, count: tables.length },
    { id: 'keyvalue', label: 'Key–value', icon: FileText, count: Object.keys(pairs).length },
    { id: 'text', label: 'Raw text', icon: Code, count: text ? 1 : 0 },
  ];

  return (
    <>
      <div className="cp-toolbar">
        <div className="cp-tabs" role="tablist" aria-label="Extraction views">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={activeTab === t.id}
                className={activeTab === t.id ? 'cp-active' : ''}
                onClick={() => setActiveTab(t.id)}
              >
                <Icon size={13} />
                {t.label}
                <span className={`cp-tab-count${t.count === 0 ? ' cp-warn' : ''}`}>{t.count}</span>
              </button>
            );
          })}
        </div>
        <div className="cp-toolbar-spacer" />
        {extractedData?.pageCount ? <div className="cp-meta">{extractedData.pageCount} pages read</div> : null}
      </div>

      <div style={{ padding: 18 }}>
        {activeTab === 'tables' ? (
          tables.length ? (
            <div style={{ display: 'grid', gap: 16 }}>
              {tables.map((table, i) => (
                <TableView key={i} index={i} table={table} />
              ))}
            </div>
          ) : (
            <ConfigEmptyState
              title="No tables found"
              body="The extractor read the file but recognised no tabular structure. Try a different extraction method on the previous step, or preprocess the PDF if it is a poor scan."
            />
          )
        ) : null}

        {activeTab === 'keyvalue' ? (
          Object.keys(pairs).length ? (
            <div className="it-frame">
              <table className="it-tbl">
                <thead><tr><th>Key</th><th>Value</th></tr></thead>
                <tbody>
                  {Object.entries(pairs).map(([key, value]) => (
                    <tr key={key} className="cp-row">
                      <td className="cp-cell"><span className="cp-name">{key}</span></td>
                      <td className="cp-cell"><span className="cp-mono">{String(value)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <ConfigEmptyState title="No key–value pairs" body="Nothing in this document was recognised as a labelled field." />
          )
        ) : null}

        {activeTab === 'text' ? (
          text ? (
            <pre className="ep-raw">{text}</pre>
          ) : (
            <ConfigEmptyState title="No raw text" body="This extraction method did not return a text layer." />
          )
        ) : null}
      </div>

      {/* Navigation lives in the wizard frame; kept for standalone callers. */}
      <div className={`justify-between ${hideFooter ? 'hidden' : 'flex'}`} style={{ padding: '0 18px 18px', gap: 8 }}>
        <button type="button" className="cp-btn" onClick={onBack}>Back</button>
        <button type="button" className="cp-btn cp-primary" onClick={onNext}>Continue</button>
      </div>
    </>
  );
}

/** One extracted table, with the source row numbers preserved. */
function TableView({ table, index }) {
  const rows = table?.data || [];
  if (!rows.length) {
    return (
      <div className="ep-panel">
        <div className="cp-help">Table {index + 1} came back empty.</div>
      </div>
    );
  }
  const headers = rows[0] || [];
  const body = rows.slice(1);

  return (
    <div>
      <div className="ep-section-head">
        <h3>Table {index + 1}</h3>
        <p>{body.length.toLocaleString()} rows × {headers.length} columns</p>
      </div>
      <div className="it-frame">
        <div className="it-scroll" style={{ '--it-max-h': '48vh' }}>
          <table className="it-tbl">
            <thead>
              <tr>
                <th style={{ width: 56 }}>Row</th>
                {headers.map((header, i) => (
                  <th key={i}>
                    <div>Col {i + 1}</div>
                    {header ? (
                      <div style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: 'var(--lodge-onyx-700)' }}>
                        {header}
                      </div>
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, rowIndex) => (
                <tr key={rowIndex} className="cp-row it-row-hover">
                  {/* +2: header is row 1, and the source file is 1-indexed. */}
                  <td className="cp-cell"><span className="cp-code">{rowIndex + 2}</span></td>
                  {headers.map((_, colIndex) => (
                    <td key={colIndex} className="cp-cell">
                      <span className="cp-mono">{row[colIndex] || ''}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
